import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  timeout: 15000,
  withCredentials: true,
});

// Reuse the existing exported api instance below.

// Attach the Store's existing login token for Creator Hub community requests.
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("lostdc:token");
  if (token && !config.headers?.Authorization) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  }
  return config;
});


export const fetchServerStatus = () => client.get("/server-status").then(r => r.data);
export const fetchStats = () => client.get("/stats").then(r => r.data);

export const fetchDiscordLive = () => client.get("/discord/live").then(r => r.data);
export const fetchServers = () => client.get("/servers").then(r => r.data);
export const fetchOnlinePlayers = () => client.get("/players/online").then(r => r.data);
export const fetchLivePlayers = () => client.get("/players/live").then(r => r.data);
export const fetchEvents = (params = {}) => {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)).toString();
  return client.get(`/events${q ? `?${q}` : ""}`).then(r => r.data);
};
export const fetchEvent = (slug) => client.get(`/events/${slug}`).then(r => r.data);
export const fetchEventCategories = () => client.get("/events/categories").then(r => r.data);
export const searchEvents = (q) => client.get(`/events/search?q=${encodeURIComponent(q)}`).then(r => r.data);
export const reportEvent = (slug, payload) => client.post(`/events/${slug}/report`, payload).then(r => r.data);
export const registerTeam = (slug, payload) => client.post(`/events/${slug}/teams`, payload).then(r => r.data);
export const fetchTeams = (slug) => client.get(`/events/${slug}/teams`).then(r => r.data);

// -------- Communities --------
export const fetchCommunities = (params = {}) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)).toString();
  return client.get(`/communities${qs ? `?${qs}` : ""}`).then(r => r.data);
};
export const fetchCommunity = (slug) => client.get(`/communities/${slug}`).then(r => r.data);
export const fetchCommunityCategories = () => client.get("/communities/categories").then(r => r.data);
export const createCommunity = (payload) => client.post("/communities", payload).then(r => r.data);
export const updateCommunity = (slug, payload) => client.put(`/communities/${slug}`, payload).then(r => r.data);
export const deleteCommunity = (slug) => client.delete(`/communities/${slug}`).then(r => r.data);
export const joinCommunity = (slug) => client.post(`/communities/${slug}/join`).then(r => r.data);
export const leaveCommunity = (slug) => client.delete(`/communities/${slug}/join`).then(r => r.data);
export const fetchCommunityMembers = (slug, params = {}) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)).toString();
  return client.get(`/communities/${slug}/members${qs ? `?${qs}` : ""}`).then(r => r.data);
};
export const setCommunityRole = (slug, user_id, role) => client.put(`/communities/${slug}/members/${user_id}/role`, { role }).then(r => r.data);
export const kickCommunityMember = (slug, user_id) => client.delete(`/communities/${slug}/members/${user_id}`).then(r => r.data);
export const fetchCommunityMedia = (slug) => client.get(`/communities/${slug}/media`).then(r => r.data);
export const fetchCommunityAnalytics = (slug) => client.get(`/communities/${slug}/analytics`).then(r => r.data);
export const pinCommunityPost = (slug, post_id) => client.post(`/communities/${slug}/posts/${post_id}/pin`).then(r => r.data);
export const featureCommunityPost = (slug, post_id) => client.post(`/communities/${slug}/posts/${post_id}/feature`).then(r => r.data);
export const hideCommunityPost = (slug, post_id) => client.delete(`/communities/${slug}/posts/${post_id}`).then(r => r.data);
export const fetchBuilds = () => client.get("/builds").then(r => r.data);

export const fetchFeaturedBuilds = () =>
  client
    .get("/builds/featured/current")
    .then((r) => r.data);

export const fetchFeaturedBuildHistory = (period = "", limit = 100) => {
  const params = new URLSearchParams();

  if (period) params.set("period", period);
  params.set("limit", String(limit));

  return client
    .get(`/builds/featured/history?${params.toString()}`, {
      headers: adminHeaders(),
    })
    .then((r) => r.data);
};

export const manuallyFeatureBuild = (payload) =>
  client
    .post("/builds/featured/manual", payload, {
      headers: adminHeaders(),
    })
    .then((r) => r.data);

export const automaticallyFeatureBuild = (payload) =>
  client
    .post("/builds/featured/auto", payload, {
      headers: adminHeaders(),
    })
    .then((r) => r.data);


export const fetchLeaderboard = (cat) => client.get(`/leaderboards/${cat}`).then(r => r.data);
export const fetchStore = () => client.get("/store").then(r => r.data);
export const fetchAnnouncements = () => client.get("/announcements").then(r => r.data);
export const fetchActivity = () => client.get("/activity").then(r => r.data);
export const fetchSettings = () => client.get("/settings").then(r => r.data);
export const submitContact = (payload) => client.post("/contact", payload).then(r => r.data);
export const mojangLookup = (username) => client.get(`/mojang/${encodeURIComponent(username)}`).then(r => r.data);

// Creators (public)
export const fetchCreators = (featured = false) => client.get(`/creators${featured ? "?featured=true" : ""}`).then(r => r.data);
export const fetchCreator = (id) => client.get(`/creators/${id}`).then(r => r.data);

// Users (public profile)
export const fetchUser = (idOrHandle) => {
  const raw = String(idOrHandle || "");
  const path = raw.startsWith("@")
    ? `/users/${encodeURIComponent(raw)}`   // GET /users/@handle
    : `/users/${encodeURIComponent(raw)}`;
  return client.get(path).then(r => r.data);
};
export const fetchUserFollowers = (id) => client.get(`/users/${id}/followers`).then(r => r.data);
export const fetchUserFollowing = (id) => client.get(`/users/${id}/following`).then(r => r.data);
export const fetchUserMutuals = (id) => client.get(`/users/${id}/mutuals`).then(r => r.data);
export const fetchSuggestedUsers = (limit = 8) => client.get(`/users/suggested?limit=${limit}`).then(r => r.data);
export const searchUsers = (q) => client.get(`/users/search?q=${encodeURIComponent(q)}`).then(r => r.data);

// Server detail (dedicated page)
export const fetchServerDetail = (key) => client.get(`/servers/${key}`).then(r => r.data);
export const fetchServerGuide = (serverKey) =>
  client.get(`/server-guide/${encodeURIComponent(serverKey)}`).then(r => r.data);

export const searchServerGuide = (serverKey, query) =>
  client
    .get(
      `/server-guide/${encodeURIComponent(serverKey)}/search`,
      { params: { q: query } }
    )
    .then(r => r.data);


// Config (badges, socials, ranks, countries, home_sections, staff)
export const fetchConfig = () => client.get("/config").then(r => r.data);

// Feed
export const fetchFeed = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return client.get(`/feed${q ? `?${q}` : ""}`).then(r => r.data);
};
export const fetchTrendingTags = () => client.get("/feed/tags/trending").then(r => r.data);

// -------- Global Search --------
export const globalSearch = (q, type = "all", limit = 10) => {
  const params = new URLSearchParams({ q: q || "", type: type || "all", limit: String(limit) });
  return client.get(`/search?${params}`).then(r => r.data);
};
export const fetchSearchTrending = (limit = 10) => client.get(`/search/trending?limit=${limit}`).then(r => r.data);
export const logSearchQuery = (q) => client.post("/search/log", { q }).then(r => r.data).catch(() => null);
// YouTube (public)
export const fetchYoutubeVideos = (limit = 24, liveOnly = false) =>
  client.get(`/youtube/videos?limit=${limit}&live_only=${liveOnly}`).then(r => r.data);
export const fetchLiveStreams = () => client.get("/youtube/live").then(r => r.data);

// Leaderboards
export const fetchLeaderboardServers = () =>
  client.get("/leaderboards/servers").then(r => r.data);

export const fetchLeaderboardSeasons = (serverSlug) =>
  client.get(`/leaderboards/${encodeURIComponent(serverSlug)}/seasons`).then(r => r.data);

export const fetchLeaderboardSeason = (serverSlug, seasonSlug) =>
  client
    .get(`/leaderboards/${encodeURIComponent(serverSlug)}/${encodeURIComponent(seasonSlug)}`)
    .then(r => r.data);

export const fetchLeaderboardPlayerEvents = (
  serverSlug,
  seasonSlug,
  playerName
) =>
  client
    .get(
      `/leaderboards/${encodeURIComponent(serverSlug)}/${encodeURIComponent(
        seasonSlug
      )}/player/${encodeURIComponent(playerName)}/events`
    )
    .then((r) => r.data);

export const fetchLeaderboardWidget = (slug) =>
  client.get(`/leaderboards/widgets/${encodeURIComponent(slug)}`).then(r => r.data);

export const adminLeaderboardWidgets = {
  list: () =>
    client.get("/admin/leaderboards/widgets", { headers: adminHeaders() }).then(r => r.data),

  create: (payload) =>
    client.post("/admin/leaderboards/widgets", payload, { headers: adminHeaders() }).then(r => r.data),

  update: (id, payload) =>
    client.put(`/admin/leaderboards/widgets/${id}`, payload, { headers: adminHeaders() }).then(r => r.data),

  remove: (id) =>
    client.delete(`/admin/leaderboards/widgets/${id}`, { headers: adminHeaders() }).then(r => r.data),
};

// -------- Teams / BetterTeams --------
export const fetchTeamServers = () =>
  client.get("/teams/servers").then(r => r.data);

export const fetchBetterTeams = (serverId) =>
  client.get(`/teams/${encodeURIComponent(serverId)}`).then(r => r.data);

export const fetchTeamDetail = (serverId, teamId) =>
  client
    .get(`/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}`)
    .then(r => r.data);


/* =========================
   Team Hub
========================= */

export const fetchTeamHub = (serverId, teamId) =>
  client
    .get(
      `/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}/hub`
    )
    .then(r => r.data);

export const updateTeamHubSettings = (
  serverId,
  teamId,
  payload
) =>
  client
    .put(
      `/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}/hub/settings`,
      payload
    )
    .then(r => r.data);

export const uploadTeamBranding = (
  serverId,
  teamId,
  kind,
  file
) => {
  const formData = new FormData();
  formData.append("file", file);

  return client
    .post(
      `/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}/hub/branding/${encodeURIComponent(kind)}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then(r => r.data);
};

export const deleteTeamBranding = (
  serverId,
  teamId,
  kind
) =>
  client
    .delete(
      `/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}/hub/branding/${encodeURIComponent(kind)}`
    )
    .then(r => r.data);

export const searchTeamPlayers = (
  serverId,
  teamId,
  query
) =>
  client
    .get(
      `/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}/hub/player-search?q=${encodeURIComponent(query)}`
    )
    .then(r => r.data);

export const inviteTeamPlayer = (
  serverId,
  teamId,
  minecraft_username
) =>
  client
    .post(
      `/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}/hub/invite`,
      { minecraft_username }
    )
    .then(r => r.data);

export const kickTeamPlayer = (
  serverId,
  teamId,
  target_username,
  reason = ""
) =>
  client
    .post(
      `/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}/hub/kick`,
      {
        target_username,
        reason,
      }
    )
    .then(r => r.data);

export const leaveTeam = (serverId, teamId) =>
  client
    .post(
      `/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}/hub/leave`
    )
    .then(r => r.data);

export const transferTeamOwnership = (
  serverId,
  teamId,
  target_username
) =>
  client
    .post(
      `/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}/hub/transfer`,
      { target_username }
    )
    .then(r => r.data);

export const fetchTeamInvitations = () =>
  client
    .get("/teams/invitations")
    .then(r => r.data);

export const acceptTeamInvitation = (
  serverId,
  teamId
) =>
  client
    .post(
      `/teams/invitations/${encodeURIComponent(serverId)}/${encodeURIComponent(teamId)}/accept`
    )
    .then(r => r.data);

export const declineTeamInvitation = (
  serverId,
  teamId
) =>
  client
    .post(
      `/teams/invitations/${encodeURIComponent(serverId)}/${encodeURIComponent(teamId)}/decline`
    )
    .then(r => r.data);

export const adminTeams = {
  servers: {
    list: () =>
      client.get("/admin/teams/servers", { headers: adminHeaders() }).then(r => r.data),

    create: (payload) =>
      client.post("/admin/teams/servers", payload, { headers: adminHeaders() }).then(r => r.data),

    update: (id, payload) =>
      client.put(`/admin/teams/servers/${encodeURIComponent(id)}`, payload, {
        headers: adminHeaders(),
      }).then(r => r.data),

    remove: (id) =>
      client.delete(`/admin/teams/servers/${encodeURIComponent(id)}`, {
        headers: adminHeaders(),
      }).then(r => r.data),

    test: (id) =>
      client.post(`/admin/teams/servers/${encodeURIComponent(id)}/test`, null, {
        headers: adminHeaders(),
      }).then(r => r.data),

    detect: (id) =>
      client.post(`/admin/teams/servers/${encodeURIComponent(id)}/detect`, null, {
        headers: adminHeaders(),
      }).then(r => r.data),
  },

  list: (serverId) =>
    client.get(`/admin/teams/${encodeURIComponent(serverId)}/teams`, {
      headers: adminHeaders(),
    }).then(r => r.data),

  detail: (serverId, teamId) =>
    client.get(
      `/admin/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}`,
      { headers: adminHeaders() }
    ).then(r => r.data),

  website: (serverId, teamId, payload) =>
    client.put(
      `/admin/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}/website`,
      payload,
      { headers: adminHeaders() }
    ).then(r => r.data),

  plugin: (serverId, teamId, payload) =>
    client.put(
      `/admin/teams/${encodeURIComponent(serverId)}/teams/${encodeURIComponent(teamId)}/plugin`,
      payload,
      { headers: adminHeaders() }
    ).then(r => r.data),
};

// Admin
const adminHeaders = () => ({ "X-Admin-Token": localStorage.getItem("lostdc:admin") || "" });
export const adminVerify = (token) => client.post("/admin/verify", null, { headers: { "X-Admin-Token": token } }).then(r => r.data);
export const adminServers = {
  list: () => client.get("/admin/servers", { headers: adminHeaders() }).then(r => r.data),
  create: (p) => client.post("/admin/servers", p, { headers: adminHeaders() }).then(r => r.data),
  update: (id, p) => client.put(`/admin/servers/${id}`, p, { headers: adminHeaders() }).then(r => r.data),
  remove: (id) => client.delete(`/admin/servers/${id}`, { headers: adminHeaders() }).then(r => r.data),
};
// -------- DrkC Eco — Server Guide / Knowledge --------
export const adminServerGuide = {
  get: (serverKey, guideId) =>
    client
      .get(
        `/admin/server-guide/${encodeURIComponent(serverKey)}/${encodeURIComponent(guideId)}`,
        {
          headers: adminHeaders(),
        }
      )
      .then((r) => r.data),


  list: (serverKey) =>
    client.get(`/admin/server-guide/${encodeURIComponent(serverKey)}`, {
      headers: adminHeaders()
    }).then(r => r.data),

  create: (serverKey, payload) =>
    client.post(`/admin/server-guide/${encodeURIComponent(serverKey)}`, payload, {
      headers: adminHeaders()
    }).then(r => r.data),

  update: (serverKey, guideId, payload) =>
    client.put(
      `/admin/server-guide/${encodeURIComponent(serverKey)}/${encodeURIComponent(guideId)}`,
      payload,
      { headers: adminHeaders() }
    ).then(r => r.data),

  remove: (serverKey, guideId) =>
    client.delete(
      `/admin/server-guide/${encodeURIComponent(serverKey)}/${encodeURIComponent(guideId)}`,
      { headers: adminHeaders() }
    ).then(r => r.data),
};

export const adminServerGuideDiscoveries = {
  list: (serverKey, params = {}) =>
    client
      .get(
        `/admin/server-guide-discoveries/${encodeURIComponent(serverKey)}`,
        {
          headers: adminHeaders(),
          params,
        }
      )
      .then((r) => r.data),

  stats: (serverKey) =>
    client
      .get(
        `/admin/server-guide-discoveries/${encodeURIComponent(serverKey)}/stats`,
        {
          headers: adminHeaders(),
        }
      )
      .then((r) => r.data),

  get: (serverKey, discoveryId) =>
    client
      .get(
        `/admin/server-guide-discoveries/${encodeURIComponent(serverKey)}/${encodeURIComponent(discoveryId)}`,
        {
          headers: adminHeaders(),
        }
      )
      .then((r) => r.data),

  update: (serverKey, discoveryId, payload) =>
    client
      .put(
        `/admin/server-guide-discoveries/${encodeURIComponent(serverKey)}/${encodeURIComponent(discoveryId)}`,
        payload,
        {
          headers: adminHeaders(),
        }
      )
      .then((r) => r.data),

  approve: (serverKey, discoveryId) =>
    client
      .post(
        `/admin/server-guide-discoveries/${encodeURIComponent(serverKey)}/${encodeURIComponent(discoveryId)}/approve`,
        null,
        {
          headers: adminHeaders(),
        }
      )
      .then((r) => r.data),

  ignore: (serverKey, discoveryId) =>
    client
      .post(
        `/admin/server-guide-discoveries/${encodeURIComponent(serverKey)}/${encodeURIComponent(discoveryId)}/ignore`,
        null,
        {
          headers: adminHeaders(),
        }
      )
      .then((r) => r.data),

  resolveConflict: (serverKey, discoveryId, resolution) =>
    client
      .post(
        `/admin/server-guide-discoveries/${encodeURIComponent(serverKey)}/${encodeURIComponent(discoveryId)}/resolve-conflict`,
        { resolution },
        {
          headers: adminHeaders(),
        }
      )
      .then((r) => r.data),

  promote: (serverKey, discoveryId) =>
    client
      .post(
        `/admin/server-guide-discoveries/${encodeURIComponent(serverKey)}/${encodeURIComponent(discoveryId)}/promote`,
        null,
        {
          headers: adminHeaders(),
        }
      )
      .then((r) => r.data),
};

export const adminAnnouncements = {
  list: () => client.get("/admin/announcements", { headers: adminHeaders() }).then(r => r.data),
  create: (p) => client.post("/admin/announcements", p, { headers: adminHeaders() }).then(r => r.data),
  update: (id, p) => client.put(`/admin/announcements/${id}`, p, { headers: adminHeaders() }).then(r => r.data),
  remove: (id) => client.delete(`/admin/announcements/${id}`, { headers: adminHeaders() }).then(r => r.data),
};
export const adminEvents = {
  list: () => client.get("/admin/events", { headers: adminHeaders() }).then(r => r.data),
  create: (p) => client.post("/admin/events", p, { headers: adminHeaders() }).then(r => r.data),
  update: (id, p) => client.put(`/admin/events/${id}`, p, { headers: adminHeaders() }).then(r => r.data),
  remove: (id) => client.delete(`/admin/events/${id}`, { headers: adminHeaders() }).then(r => r.data),
};
export const adminSaveSettings = (p) => client.put("/admin/settings", p, { headers: adminHeaders() }).then(r => r.data);

// Phase 8 — Admin Dashboard (unified)
export const adminDashboard = {
  users: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)).toString();
    return client.get(`/admin/dashboard/users${qs ? `?${qs}` : ""}`, { headers: adminHeaders() }).then(r => r.data);
  },
  userDetail: (id) => client.get(`/admin/dashboard/users/${id}`, { headers: adminHeaders() }).then(r => r.data),
  updateUser: (id, patch) => client.put(`/admin/dashboard/users/${id}`, patch, { headers: adminHeaders() }).then(r => r.data),
    walletPermissions: (id) =>
    client.get(`/admin/dashboard/users/${id}/wallet-permissions`, {
      headers: adminHeaders()
    }).then(r => r.data),

  updateWalletPermissions: (id, permissions) =>
    client.put(`/admin/dashboard/users/${id}/wallet-permissions`, permissions, {
      headers: adminHeaders()
    }).then(r => r.data),
  deleteUser: (id) => client.delete(`/admin/dashboard/users/${id}`, { headers: adminHeaders() }).then(r => r.data),
  moderationQueue: (limit = 100) => client.get(`/admin/dashboard/moderation/queue?limit=${limit}`, { headers: adminHeaders() }).then(r => r.data),
  resolveReport: (kind, id, status = "resolved") => client.post(`/admin/dashboard/moderation/${kind}/${id}/resolve`, { status }, { headers: adminHeaders() }).then(r => r.data),
  youtube: () => client.get("/admin/dashboard/youtube", { headers: adminHeaders() }).then(r => r.data),
  syncCreator: (id) => client.post(`/admin/dashboard/youtube/${id}/sync`, null, { headers: adminHeaders() }).then(r => r.data),
  analytics: () => client.get("/admin/dashboard/analytics", { headers: adminHeaders() }).then(r => r.data),
  analyticsTimeseries: (days = 14) => client.get(`/admin/dashboard/analytics/timeseries?days=${days}`, { headers: adminHeaders() }).then(r => r.data),
};

export const SERVER_IP = "play.lostdrkc.in";
export const DISCORD_URL = "https://lostdrkc.in/discord";

// -------- Shop (public) --------
export const fetchShopProducts = (params = {}) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)).toString();
  return client.get(`/shop/products${qs ? `?${qs}` : ""}`).then(r => r.data);
};
export const fetchShopProduct = (slug) => client.get(`/shop/products/${slug}`).then(r => r.data);
export const fetchShopCategories = () => client.get("/shop/categories").then(r => r.data);
export const fetchShopConfig = () => client.get("/shop/config").then(r => r.data);

// -------- Shop (authed) --------
import { authClient } from "@/lib/auth";
export const shopCheckout = (payload) => authClient.post("/shop/checkout", payload).then(r => r.data);
export const validateShopCoupon = (payload) => authClient.post("/shop/coupon/validate", payload).then(r => r.data);
export const fetchMyOrders = () => authClient.get("/shop/orders").then(r => r.data);
export const fetchMyOrder = (n) => authClient.get(`/shop/orders/${n}`).then(r => r.data);
export const fetchOrderDelivery = (n) => authClient.get(`/shop/orders/${n}/delivery`).then(r => r.data);
export const pollPaymentStatus = (n, token) => client.get(`/shop/orders/${n}/payment-status?confirm_token=${encodeURIComponent(token)}`).then(r => r.data);

// -------- DC Coins wallet (authed) --------
export const fetchWallet = () => authClient.get("/coins/wallet").then(r => r.data);
export const setCoinPin = (pin) => authClient.post("/coins/pin", { pin }).then(r => r.data);
export const fetchCoinTransactions = () => authClient.get("/coins/transactions").then(r => r.data);
export const searchCoinRecipients = (q) => authClient.get(`/coins/users/search?q=${encodeURIComponent(q)}`).then(r => r.data);
export const sendCoins = (payload) => authClient.post("/coins/send", payload).then(r => r.data);

// -------- Admin: Shop + Coins (X-Admin-Token) --------
export const adminShop = {
  products: () => client.get("/admin/shop/products", { headers: adminHeaders() }).then(r => r.data),

  coupons: () =>
    client.get("/admin/shop/coupons", {
      headers: adminHeaders()
    }).then(r => r.data),

  createCoupon: (payload) =>
    client.post("/admin/shop/coupons", payload, {
      headers: adminHeaders()
    }).then(r => r.data),

  updateCoupon: (id, payload) =>
    client.put(`/admin/shop/coupons/${id}`, payload, {
      headers: adminHeaders()
    }).then(r => r.data),

  deleteCoupon: (id) =>
    client.delete(`/admin/shop/coupons/${id}`, {
      headers: adminHeaders()
    }).then(r => r.data),

  couponUsage: (id) =>
    client.get(`/admin/shop/coupons/${id}/usage`, {
      headers: adminHeaders()
    }).then(r => r.data),
  createProduct: (p) => client.post("/admin/shop/products", p, { headers: adminHeaders() }).then(r => r.data),
  updateProduct: (id, p) => client.put(`/admin/shop/products/${id}`, p, { headers: adminHeaders() }).then(r => r.data),
  deleteProduct: (id) => client.delete(`/admin/shop/products/${id}`, { headers: adminHeaders() }).then(r => r.data),
  orders: (q = "") => client.get(`/admin/shop/orders${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers: adminHeaders() }).then(r => r.data),
  completeOrder: (n) => client.post(`/admin/shop/orders/${n}/complete`, null, { headers: adminHeaders() }).then(r => r.data),
  cancelOrder: (n) => client.post(`/admin/shop/orders/${n}/cancel`, null, { headers: adminHeaders() }).then(r => r.data),
  storeOrders: (search = "") => client.get(`/admin/shop/store-orders${search ? `?search=${encodeURIComponent(search)}` : ""}`, { headers: adminHeaders() }).then(r => r.data),
  setStoreOrderStatus: (id, status) => client.put(`/admin/shop/store-orders/${id}/status`, { status }, { headers: adminHeaders() }).then(r => r.data),
};
export const uploadImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/upload", fd, { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } }).then(r => r.data);
};
export const fileUrl = (url) => (url && url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);

export const adminIntegrations = {
  get: () => client.get("/admin/integrations", { headers: adminHeaders() }).then(r => r.data),
  save: (p) => client.put("/admin/integrations", p, { headers: adminHeaders() }).then(r => r.data),
  wooTest: () => client.post("/admin/integrations/woo/test", null, { headers: adminHeaders() }).then(r => r.data),
  wooImportProducts: () => client.post("/admin/integrations/woo/import-products", null, { headers: adminHeaders() }).then(r => r.data),
  wooImportOrders: () => client.post("/admin/integrations/woo/import-orders", null, { headers: adminHeaders() }).then(r => r.data),
  mysqlTest: () => client.post("/admin/integrations/mysql/test", null, { headers: adminHeaders() }).then(r => r.data),
  mysqlEnsureTable: () => client.post("/admin/integrations/mysql/ensure-table", null, { headers: adminHeaders() }).then(r => r.data),
  testDcCoins: (id) => client.get(`/admin/integrations/test-dc-coins?lostdc_id=${encodeURIComponent(id)}`, { headers: adminHeaders() }).then(r => r.data),
};

export const adminCoins = {
  users: (q = "") => client.get(`/admin/coins/users${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers: adminHeaders() }).then(r => r.data),
  setBalance: (uid, dc_coin) => client.put(`/admin/coins/users/${uid}`, { dc_coin }, { headers: adminHeaders() }).then(r => r.data),
  getSettings: () => client.get("/admin/coins/settings", { headers: adminHeaders() }).then(r => r.data),
  setSettings: (dc_coin_value) => client.put("/admin/coins/settings", { dc_coin_value }, { headers: adminHeaders() }).then(r => r.data),
  transactions: (q = "") => client.get(`/admin/coins/transactions${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers: adminHeaders() }).then(r => r.data),
  syncAccounts: () => client.get("/admin/coins/sync/accounts", { headers: adminHeaders() }).then(r => r.data),
  addSyncAccount: (p) => client.post("/admin/coins/sync/accounts", p, { headers: adminHeaders() }).then(r => r.data),
  delSyncAccount: (id) => client.delete(`/admin/coins/sync/accounts/${encodeURIComponent(id)}`, { headers: adminHeaders() }).then(r => r.data),
  runSync: () => client.post("/admin/coins/sync/run", null, { headers: adminHeaders() }).then(r => r.data),
  syncLogs: () => client.get("/admin/coins/sync/logs", { headers: adminHeaders() }).then(r => r.data),
  syncPending: () => client.get("/admin/coins/sync/pending", { headers: adminHeaders() }).then(r => r.data),
  // Rewards (multi-select players)
  sendRewards: (payload) => client.post("/admin/coins/rewards", payload, { headers: adminHeaders() }).then(r => r.data),
  rewardsHistory: () => client.get("/admin/coins/rewards/history", { headers: adminHeaders() }).then(r => r.data),
  // Giveaway (fund creators)
  giveawayCreators: (q = "") => client.get(`/admin/coins/giveaway/creators${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers: adminHeaders() }).then(r => r.data),
  giveawayGrant: (payload) => client.post("/admin/coins/giveaway/grant", payload, { headers: adminHeaders() }).then(r => r.data),
  giveawayHistory: () => client.get("/admin/coins/giveaway/history", { headers: adminHeaders() }).then(r => r.data),
};

// Creator giveaway pool
export const fetchGiveawayWallet = () => authClient.get("/coins/giveaway/wallet").then(r => r.data);
export const sendGiveaway = (payload) => authClient.post("/coins/giveaway/send", payload).then(r => r.data);
export const fetchGiveawayHistory = () => authClient.get("/coins/giveaway/history").then(r => r.data);

// Raw axios instance (bearer-attached) for ported LIVE Pluse components.
export const api = authClient;

/* =========================
   Leaderboard Events
========================= */

export const adminLeaderboardEvents = {
  list: (seasonId) =>
    client
      .get(`/admin/leaderboards/events?season_id=${encodeURIComponent(seasonId)}`, {
        headers: adminHeaders(),
      })
      .then((r) => r.data),

  get: (eventId) =>
    client
      .get(`/admin/leaderboards/events/${eventId}`, {
        headers: adminHeaders(),
      })
      .then((r) => r.data),

  create: (payload) =>
    client
      .post("/admin/leaderboards/events", payload, {
        headers: adminHeaders(),
      })
      .then((r) => r.data),

  update: (eventId, payload) =>
    client
      .put(`/admin/leaderboards/events/${eventId}`, payload, {
        headers: adminHeaders(),
      })
      .then((r) => r.data),

  remove: (eventId) =>
    client
      .delete(`/admin/leaderboards/events/${eventId}`, {
        headers: adminHeaders(),
      })
      .then((r) => r.data),

  points: (eventId) =>
    client
      .get(`/admin/leaderboards/events/${eventId}/points`, {
        headers: adminHeaders(),
      })
      .then((r) => r.data),

  addPoint: (eventId, payload) =>
    client
      .post(`/admin/leaderboards/events/${eventId}/points`, payload, {
        headers: adminHeaders(),
      })
      .then((r) => r.data),

  updatePoint: (eventId, pointId, payload) =>
    client
      .put(`/admin/leaderboards/events/${eventId}/points/${pointId}`, payload, {
        headers: adminHeaders(),
      })
      .then((r) => r.data),

  removePoint: (eventId, pointId) =>
    client
      .delete(`/admin/leaderboards/events/${eventId}/points/${pointId}`, {
        headers: adminHeaders(),
      })
      .then((r) => r.data),

  replacePoints: (eventId, points) =>
    client
      .put(`/admin/leaderboards/events/${eventId}/points`, points, {
        headers: adminHeaders(),
      })
      .then((r) => r.data),
};
    
// PlayZone — Home page widgets
export const fetchPlayZoneLeaderboards = () =>
  client.get("/playzone/leaderboards").then(r => r.data);

export const fetchPlayZoneActivity = () =>
  client.get("/playzone/activity").then(r => r.data);
